import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  acceptOrganizationApi,
  changeOrganizationApi,
  createOrgApi,
  declineOrganizationApi,
  deleteMemberApi,
  deleteOrganizationApi,
  fetchCurrentOrganization,
  getOrganizationMembersApi,
  getOrganizationsListApi,
  inviteMembersApi,
  joinOrgApi,
  leaveOrgApi,
  softDeleteInviteByInviteeApi,
  softDeleteInviteByInviterApi,
  transferOwnershipApi,
  updateOrganizationNameApi,
} from '~/api/organization';
import type { Organization, OrganizationsMember } from '~/types/organization';
import { isDeleteOrganizationRefreshNeeded } from '~/types/organization';

/**
 * 切換組織時「換新 token」這一步失敗的標記。
 * 呼叫端據此顯示「切換組織失敗」，而不是把這串內部訊息直接丟給使用者看。
 */
export const ORG_SWITCH_TOKEN_REFRESH_FAILED = 'ORG_SWITCH_TOKEN_REFRESH_FAILED';

export const useOrganizationStore = defineStore('organization', () => {
  const profileStore = useProfileStore();
  const currentOrganization = ref<OrganizationsMember | null>(null);
  const organizationsList = ref<Organization[]>([]);
  const members = ref<OrganizationsMember[]>([]);
  const loading = ref(false);
  // 切換組織進行中：用來顯示全域遮罩，阻擋使用者在換組織期間操作畫面
  const isSwitchingOrganization = ref(false);
  const currentOrganizationPromise = ref<Promise<OrganizationsMember> | null>(null);
  const lastFetchFailedAt = ref<number | null>(null);
  const lastFetchError = ref<unknown>(null);
  const FETCH_ERROR_COOLDOWN_MS = 3000;
  // computed
  const isAdmin = computed(() => currentOrganization.value?.is_admin ?? false);
  const organizationName = computed(() => currentOrganization.value?.organizations_name);
  const organizationUuid = computed(() => currentOrganization.value?.organizations_uuid);

  // actions
  async function getCurrentOrganization(options?: { force?: boolean }) {
    if (!options?.force && currentOrganization.value) {
      return currentOrganization.value;
    }

    if (!options?.force && currentOrganizationPromise.value) {
      return await currentOrganizationPromise.value;
    }

    if (
      !options?.force &&
      lastFetchFailedAt.value &&
      Date.now() - lastFetchFailedAt.value < FETCH_ERROR_COOLDOWN_MS
    ) {
      throw lastFetchError.value ?? new Error('Failed to fetch current organization recently');
    }

    currentOrganizationPromise.value = fetchCurrentOrganization()
      .then((org) => {
        currentOrganization.value = org;
        lastFetchFailedAt.value = null;
        lastFetchError.value = null;
        return org;
      })
      .catch((error) => {
        lastFetchFailedAt.value = Date.now();
        lastFetchError.value = error;
        throw error;
      })
      .finally(() => {
        currentOrganizationPromise.value = null;
      });

    return await currentOrganizationPromise.value;
  }

  async function joinOrganization(organizationUuid: string) {
    return await joinOrgApi(organizationUuid);
  }

  /**
   * 建立組織。
   *
   * 建立成功的當下，後端的 current organization 就已經是這個新組織了 —— 對本來就有組織的人
   * 來說，這等同於一次組織切換，所以「換不到新 scope」的後果也一樣：
   * 畫面是新組織、token 還是舊組織，之後每一支 API 都會讀到錯的組織。
   * 處理方式比照 setCurrentOrganization：不讓使用者回到可操作的畫面，重新登入把三者對齊。
   */
  async function createOrganization(name: string) {
    currentOrganization.value = await createOrgApi(name);

    const refreshed = await useAuth()
      .refreshTokenAfterOrgSwitch(currentOrganization.value?.organizations_uuid)
      .catch((error) => {
        console.warn('Token refresh threw after organization creation:', error);
        return false;
      });

    if (!refreshed) {
      await useAuth().forceLogoutToLogin();
      throw new Error(ORG_SWITCH_TOKEN_REFRESH_FAILED);
    }

    // 讓側邊欄的組織下拉立刻看得到新組織。這步失敗不影響已經建好的組織，
    // 下次進頁面自然會重拉，所以只記錄不中斷。
    await fetchOrganizationsList().catch((error) => {
      console.warn('Failed to refresh organizations list after creation:', error);
    });

    return currentOrganization.value;
  }

  function clearOrganization() {
    currentOrganization.value = null;
    currentOrganizationPromise.value = null;
    lastFetchFailedAt.value = null;
    lastFetchError.value = null;
  }

  async function fetchOrganizationsList() {
    organizationsList.value = await getOrganizationsListApi();
    return organizationsList.value;
  }

  async function getOrganizationsList() {
    if (organizationsList.value.length > 0) {
      return organizationsList.value;
    }
    organizationsList.value = await fetchOrganizationsList();
    return organizationsList.value;
  }

  function beginOrganizationSwitch() {
    isSwitchingOrganization.value = true;
  }

  function endOrganizationSwitch() {
    isSwitchingOrganization.value = false;
  }

  /**
   * 切換組織。
   *
   * ## 為什麼要分成「change 之前」與「change 之後」兩段
   * `changeOrganizationApi()` 一旦成功，**後端的 current organization 就已經換了**，
   * 前端退不回去。這之後任何一步失敗都不可以只跳個 toast 就把使用者放回舊畫面——
   * 那個畫面是舊組織，但 server 端已經是新組織，之後任何一次 token 續期都會讓
   * scope 悄悄跳過去，變成「畫面舊、資料新」的錯組織操作。
   *
   * 所以 change 之前失敗＝什麼都沒發生，收掉遮罩讓使用者留在原地；
   * change 之後失敗＝進入明確的 recovery，不回到可操作的舊畫面。
   */
  async function setCurrentOrganization(organizationUuid: string) {
    beginOrganizationSwitch();

    let authResponse: Awaited<ReturnType<typeof changeOrganizationApi>>;
    try {
      // 切換組織 API 會返回認證令牌資訊
      authResponse = await changeOrganizationApi(organizationUuid);
    } catch (error) {
      // 後端還沒切，session 沒被動到 —— 收掉遮罩，使用者留在原本的組織繼續用
      endOrganizationSwitch();
      throw error;
    }

    // ===== 以下：後端的 current organization 已經換了，沒有退路 =====

    // 更新認證令牌
    try {
      const authStore = useAuthStore();
      authStore.setTokens(authResponse.token, authResponse.refresh_token);
    } catch (error) {
      console.warn('Failed to update tokens after organization switch:', error);
      // 這裡失敗不影響下一步：refreshTokenAfterOrgSwitch() 會自己讀 cookie 裡的 refresh token
    }

    // 後端要求：change/current 回的 token 還沒帶上新組織的 scope，一定要再換一次，
    // 後續 API（agents / knowledge / analytics …）才會讀到新組織的資料。
    // 帶上 organizationUuid：換回來的 token 若仍是別的組織，這裡就要當成切換失敗，
    // 不能寫進 cookie —— 那會變成「畫面新組織、資料舊組織」而且毫無錯誤跡象。
    const refreshed = await useAuth()
      .refreshTokenAfterOrgSwitch(organizationUuid)
      .catch((error) => {
        console.warn('Token refresh threw after organization switch:', error);
        return false;
      });

    if (!refreshed) {
      // 拿不到新 scope。整頁重載救不了：scope 是綁在 token 上的，重載只會再讀到同一張換不動的
      // token，畫面照樣是舊組織而 server 端已經是新的。唯一能讓「畫面 / current organization /
      // token scope」三者重新一致的方式是重新登入。
      // 遮罩**故意不收掉**：導向 /login 之前，不可以讓使用者回到舊畫面繼續操作。
      await useAuth().forceLogoutToLogin();
      throw new Error(ORG_SWITCH_TOKEN_REFRESH_FAILED);
    }

    // 重新獲取當前組織資訊
    try {
      currentOrganization.value = await fetchCurrentOrganization();
    } catch (error) {
      console.warn('Failed to fetch current organization after switch:', error);
      // token 已經是新組織的了，這時留著舊的 currentOrganization 會讓畫面顯示錯的組織。
      // 清掉交給呼叫端的整頁重載重新讀 —— 寧可「不知道是哪個組織」也不要「顯示錯的組織」。
      clearOrganization();
    }

    // 成功時遮罩會一路留到呼叫端導頁（整頁重載），
    // 避免中間閃出一段可以點擊、但資料還是舊組織的畫面
    return currentOrganization.value;
  }

  async function leaveOrganization(organizationUuid: string) {
    const leftCurrentOrganization =
      currentOrganization.value?.organizations_uuid === organizationUuid;
    const authResponse = await leaveOrgApi(organizationUuid);

    try {
      const authStore = useAuthStore();
      authStore.setTokens(authResponse.token, authResponse.refresh_token);
    } catch (error) {
      console.warn('Failed to update tokens after leaving organization:', error);
    }

    // 離開組織後後端會把 current 換成別的組織，和切換組織一樣要重換 token 才有新 scope。
    // 換到哪一個是後端決定的，前端無從預期，所以不帶 expectedOrgUuid 去驗證 scope。
    const refreshed = await useAuth()
      .refreshTokenAfterOrgSwitch()
      .catch((error) => {
        console.warn('Failed to refresh token after leaving organization:', error);
        return false;
      });

    // 離開的是「目前這個組織」而且換不到新 scope：跟切換組織同一個問題 ——
    // 畫面還停在一個人已經不在的組織，server 端的 current 卻已經換掉了。
    // 退不回去（人真的離開了），只能重新登入讓三者重新一致。
    // 離開的是別的組織時 current 沒動，refresh 失敗不影響 scope，記錄就好。
    if (!refreshed) {
      if (leftCurrentOrganization) {
        await useAuth().forceLogoutToLogin();
        throw new Error(ORG_SWITCH_TOKEN_REFRESH_FAILED);
      }
      console.warn('Token refresh failed after leaving organization');
    }

    organizationsList.value = organizationsList.value.filter(
      (organization) => organization.organizations_uuid !== organizationUuid
    );

    if (leftCurrentOrganization) {
      clearOrganization();
      members.value = [];
    }

    return { organizationsList: organizationsList.value, leftCurrentOrganization };
  }

  /**
   * 轉移組織擁有權。轉移後「自己」從 owner 降為普通成員 —— 手上這張 token 帶的
   * 仍是 owner 的權限 scope，不換掉的話整個側邊欄與所有 PermissionGuard 都會
   * 顯示一組已經不屬於自己的權限。
   *
   * 轉移只發生在 current 組織（成員清單是 current scope），組織本身沒變，
   * 所以帶 organizationUuid 去驗證換回來的 token 仍在同一個組織。
   */
  async function transferOwnership(organizationUuid: string, targetUserId: number) {
    await transferOwnershipApi(organizationUuid, targetUserId);

    // ===== 以下：後端已經改完擁有權，沒有退路 =====

    const refreshed = await useAuth()
      .refreshTokenAfterOrgSwitch(organizationUuid)
      .catch((error) => {
        console.warn('Token refresh threw after ownership transfer:', error);
        return false;
      });

    if (!refreshed) {
      // 換不到新 scope：畫面上還是 owner 的權限，server 端已經不是了。
      // 跟切組織同一個問題，重載救不了（scope 綁在 token 上），只能重新登入。
      await useAuth().forceLogoutToLogin();
      throw new Error(ORG_SWITCH_TOKEN_REFRESH_FAILED);
    }

    // 自己的 is_admin 與整份成員清單都變了，清掉讓呼叫端重載後重新讀。
    members.value = [];
    if (currentOrganization.value?.organizations_uuid === organizationUuid) {
      currentOrganization.value = { ...currentOrganization.value, is_admin: false };
    }
  }

  /**
   * 刪除組織（僅 owner）。後端軟刪除，並比照 leave 把 current 切到剩餘組織
   * （或在沒有剩餘組織時清空），回新 token。
   *
   * @param acknowledgedBalance 該組織實查餘額 > 0 時必須帶（BE #883 的知情確認），
   *                            查不到就不帶，改由 409 帶回實值後重試。
   * @returns deletedCurrentOrganization 刪掉的是不是當前組織（呼叫端據此決定導頁）
   *          hasRemainingOrganization  刪完還有沒有別的組織（沒有就得回 setup）
   */
  async function deleteOrganization(organizationUuid: string, acknowledgedBalance?: number) {
    const deletedCurrentOrganization =
      currentOrganization.value?.organizations_uuid === organizationUuid;
    // 餘額 > 0 時後端要求把實查值送回當知情確認；不合則 409 balance_ack_required，
    // 在這行就拋出來，組織還沒被刪，呼叫端可以帶新金額重試。
    const response = await deleteOrganizationApi(organizationUuid, acknowledgedBalance);

    // ===== 以下：組織已經刪掉了，沒有退路 =====

    if (isDeleteOrganizationRefreshNeeded(response)) {
      // 後端刪成功但重發 token 失敗：手上這張 token 的 scope 指向一個已經不存在的
      // 組織，而 server 端的 current 已經換走。沒有新 token 可用、也換不回去，
      // 唯一乾淨的出口是重新登入。
      await useAuth().forceLogoutToLogin();
      throw new Error(ORG_SWITCH_TOKEN_REFRESH_FAILED);
    }

    try {
      const authStore = useAuthStore();
      authStore.setTokens(response.token, response.refresh_token);
    } catch (error) {
      console.warn('Failed to update tokens after deleting organization:', error);
    }

    // 與 leave 相同：後端自己挑下一個 current，前端無從預期，不帶 expectedOrgUuid。
    const refreshed = await useAuth()
      .refreshTokenAfterOrgSwitch()
      .catch((error) => {
        console.warn('Failed to refresh token after deleting organization:', error);
        return false;
      });

    organizationsList.value = organizationsList.value.filter(
      (organization) => organization.organizations_uuid !== organizationUuid
    );
    const hasRemainingOrganization = organizationsList.value.some(
      (organization) => organization.is_access
    );

    if (!refreshed) {
      // 刪掉的是當前組織：畫面停在一個已經不存在的組織上，只能重新登入。
      // 刪的是別的組織時 current 沒動，refresh 失敗不影響 scope，記錄就好。
      if (deletedCurrentOrganization) {
        await useAuth().forceLogoutToLogin();
        throw new Error(ORG_SWITCH_TOKEN_REFRESH_FAILED);
      }
      console.warn('Token refresh failed after deleting organization');
    }

    if (deletedCurrentOrganization) {
      clearOrganization();
      members.value = [];
    }

    return { deletedCurrentOrganization, hasRemainingOrganization };
  }

  async function getOrganizationMembers() {
    if (members.value.length > 0) {
      return members.value;
    }
    members.value = await getOrganizationMembersApi();
    return members.value;
  }

  async function fetchOrganizationMembers() {
    members.value = await getOrganizationMembersApi();
    return members.value;
  }

  async function updateOrganizationName(organizationUuid: string, name: string) {
    const response = await updateOrganizationNameApi(organizationUuid, name);
    if (currentOrganization.value) {
      currentOrganization.value.organizations_name = name;
    }
    organizationsList.value = organizationsList.value.map((organization) => {
      if (organization.organizations_uuid === organizationUuid) {
        organization.name = name;
      }
      return organization;
    });
    return response;
  }

  async function deleteMember(member: OrganizationsMember) {
    await deleteMemberApi({ uid: member.uid, id: member.id });
    members.value = members.value.filter((item) => {
      if (member.uid && member.uid !== 0) {
        return item.uid !== member.uid;
      }
      return item.id !== member.id;
    });
  }

  async function inviteMembers(emails: string[]) {
    await inviteMembersApi(emails);
  }

  async function acceptOrganization(organizationToken: string) {
    await acceptOrganizationApi(organizationToken);
  }

  async function declineOrganization(organizationUuid: string) {
    await declineOrganizationApi(organizationUuid);
  }

  /**
   * 邀請方軟刪除邀請
   * @param member 要刪除的成員
   */
  async function softDeleteInviteByInviter(member: OrganizationsMember) {
    // 如果 uid 為 0（用戶尚未註冊），則使用 id
    const targetUid = member.uid !== 0 ? member.uid : member.id;
    await softDeleteInviteByInviterApi(member.organizations_uuid, targetUid);
    // 從本地列表中移除該成員
    members.value = members.value.filter((item) => item.id !== member.id);
  }

  /**
   * 被邀請方軟刪除邀請（在被邀請方的組織列表中調用）
   * @param organizationUuid 組織 UUID
   */
  async function softDeleteInviteByInvitee(organizationUuid: string) {
    await softDeleteInviteByInviteeApi(organizationUuid);
    // 從組織列表中移除該組織
    organizationsList.value = organizationsList.value.filter(
      (org) => org.organizations_uuid !== organizationUuid
    );
  }

  /**
   * 重新邀請成員（適用於 rejected/expired 狀態）
   * @param email 成員的電子郵件
   */
  async function reinviteMember(email: string) {
    await inviteMembersApi([email]);
    // 重新獲取成員列表
    await fetchOrganizationMembers();
  }

  const reset = () => {
    clearOrganization();
    organizationsList.value = [];
    members.value = [];
    loading.value = false;
    isSwitchingOrganization.value = false;
  };

  return {
    currentOrganization,
    isAdmin,
    organizationName,
    organizationUuid,
    organizationsList,
    members,
    loading,
    isSwitchingOrganization,
    getCurrentOrganization,
    setCurrentOrganization,
    beginOrganizationSwitch,
    endOrganizationSwitch,
    joinOrganization,
    createOrganization,
    clearOrganization,
    getOrganizationsList,
    fetchOrganizationsList,
    leaveOrganization,
    transferOwnership,
    deleteOrganization,
    getOrganizationMembers,
    fetchOrganizationMembers,
    updateOrganizationName,
    deleteMember,
    inviteMembers,
    acceptOrganization,
    declineOrganization,
    softDeleteInviteByInviter,
    softDeleteInviteByInvitee,
    reinviteMember,
    reset,
  };
});
